import React, { useRef, useState } from "react";import { Link } from "react-router-dom";
import { Download, Mail, Sparkles, Heart, Flame, Wind, Hand, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import {
  StoryPosterCanvas,
  StoryP,
  Gold,
  Em,
  downloadPosterAtSize,
  downloadAsPDF,
  emailToPrinter,
  SAGE_GRADIENT,
  IVORY,
  GOLD_GRAD,
} from "./Marketing";
import { downloadAsSvg } from "../utils/vectorExports";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SilverLogo from "../components/SilverLogo";
import { useSite, resolveImageUrl } from "../context/SiteContext";

// — Spa services menu —
const SPA_SERVICES = [
  { id: "swedish",    icon: Wind,     name: "Swedish Massage",      duration: "60 / 90 min", price: "$95 / $135", blurb: "Long, gliding strokes that ease tension and quiet the nervous system. Perfect for first visits." },
  { id: "deeptissue", icon: Hand,     name: "Deep Tissue Massage",  duration: "60 / 90 min", price: "$110 / $150", blurb: "Slow, intentional pressure for the layers below the surface — chronic knots, posture strain, stubborn shoulders." },
  { id: "hotstone",   icon: Flame,    name: "Hot Stone Massage",    duration: "75 min",      price: "$140", blurb: "Heated basalt stones melt deep tension. The kind of warmth your body remembers for days." },
  { id: "facial",     icon: Sparkles, name: "Facial Cupping",       duration: "45 min",      price: "$90", blurb: "Gentle suction lifts and brightens — a natural, no-needle face treatment that calms and renews." },
  { id: "single",     icon: Heart,    name: "Signature Single",     duration: "60 min",      price: "$95", blurb: "A bespoke session — your therapist reads the room and meets you exactly where your body is today." },
];

// — Spa-themed short story posters —
const SPA_STORIES = [
  {
    id: "spa-arrived",
    slug: "she-finally-arrived",
    eyebrow: "Let Go Wellness · Spa",
    title: "She finally arrived",
    titleStr: "She finally arrived",
    footerLine: <>Some bodies need permission<br />to be still.</>,
    footerCta: "Spa & somatic care · letgowelllness.com",
    tag: "Spa",
    body: (
      <>
        <StoryP>She sat in the parking lot for ten minutes before going in.</StoryP>
        <StoryP mt="0.08in">Not because she was nervous. Because she hadn&apos;t stopped moving in eleven days, and the silence of the car felt unfamiliar.</StoryP>
        <StoryP mt="0.08in">
          The receptionist offered tea. The robe was soft. The room smelled like eucalyptus.
        </StoryP>
        <StoryP mt="0.08in">
          The therapist said, <Gold>&ldquo;You don&apos;t have to do anything but breathe.&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">She tried.</StoryP>
        <StoryP mt="0.08in">Somewhere in the middle of a long warm stroke down her back, she realized she had been holding her breath since Tuesday.</StoryP>
        <StoryP mt="0.08in">
          When she sat up, the world was a little softer at the edges. <Em>&ldquo;I forgot what this felt like,&rdquo;</Em> she said.
        </StoryP>
      </>
    ),
    caption: `She sat in the parking lot for ten minutes before going in.

Not because she was nervous. Because she hadn't stopped moving in eleven days.

The robe was soft. The room smelled like eucalyptus.

"You don't have to do anything but breathe."

She realized halfway through that she'd been holding her breath since Tuesday.

Some bodies need permission to be still. The spa is that permission.

↓ Book a session · letgowelllness.com

#spa #massagetherapy #selfcare #wellness #relaxation #ctspa #waterburyct #letgowellness #bodywork`,
  },
  {
    id: "spa-stone",
    slug: "the-warmth-she-forgot",
    eyebrow: "Let Go Wellness · Hot Stone",
    title: "The warmth she forgot",
    titleStr: "The warmth she forgot",
    footerLine: <>Heat is medicine.<br />So is being cared for.</>,
    footerCta: "Hot stone massage · letgowelllness.com",
    tag: "Hot Stone",
    body: (
      <>
        <StoryP>She had been cold for three winters.</StoryP>
        <StoryP mt="0.08in">Not the kind a sweater fixes — the deep kind, settled in her shoulders, in her lower back, in the small bones of her wrists from too many hours at a keyboard.</StoryP>
        <StoryP mt="0.08in">The therapist placed the first stone between her shoulder blades.</StoryP>
        <StoryP mt="0.08in">She made a sound she didn&apos;t know she could make — a long, low exhale that her body had been waiting years to release.</StoryP>
        <StoryP mt="0.08in">
          By the second stone, her jaw unclenched. By the fifth, she had forgotten what day it was.
        </StoryP>
        <StoryP mt="0.08in">
          She drove home with the heat off. <Em>&ldquo;I&apos;m warm enough,&rdquo;</Em> she said, almost surprised.
        </StoryP>
      </>
    ),
    caption: `She had been cold for three winters.

Not the kind a sweater fixes. The deep kind — settled in her shoulders, in her lower back, in too many hours at a keyboard.

The therapist placed the first stone between her shoulder blades.

She made a sound she didn't know she could make.

Heat is medicine. So is being cared for.

↓ Hot stone massage · letgowelllness.com

#hotstone #hotstonemassage #spaday #massage #selfcare #wellness #ctspa #waterburyct #letgowellness`,
  },
  {
    id: "spa-facial",
    slug: "the-face-shed-been-holding",
    eyebrow: "Let Go Wellness · Facial Cupping",
    title: <>The face she&apos;d been holding</>,
    titleStr: "The face she'd been holding",
    footerLine: <>Your face is allowed to rest.<br />Even if no one is looking.</>,
    footerCta: "Facial cupping · letgowelllness.com",
    tag: "Facial",
    body: (
      <>
        <StoryP>She didn&apos;t know her face had a memory.</StoryP>
        <StoryP mt="0.08in">But when the therapist began moving the cup along her jaw, her mouth softened. She hadn&apos;t realized she&apos;d been clenching for so long.</StoryP>
        <StoryP mt="0.08in">Three months of deadlines. Two of caregiving. One of saying yes when she should have said no.</StoryP>
        <StoryP mt="0.08in">The cup moved up her cheek. Around her eye. She felt years of held-in tears finally release without falling.</StoryP>
        <StoryP mt="0.08in">
          <Gold>&ldquo;You carry so much in your face,&rdquo;</Gold> the therapist said gently.
        </StoryP>
        <StoryP mt="0.08in">She nodded. Quietly. The kind of nod that doesn&apos;t need words.</StoryP>
      </>
    ),
    caption: `She didn't know her face had a memory.

Three months of deadlines. Two of caregiving. One of saying yes when she should have said no.

The cup moved up her cheek. Around her eye.
She felt years of held-in tears finally release without falling.

"You carry so much in your face."

Your face is allowed to rest. Even if no one is looking.

↓ Facial cupping · letgowelllness.com

#facialcupping #naturalbeauty #facialmassage #spa #selfcare #wellness #ctspa #waterburyct #letgowellness`,
  },
  {
    id: "spa-deeptissue",
    slug: "the-shoulder-she-forgot",
    eyebrow: "Let Go Wellness · Deep Tissue",
    title: <>The shoulder she forgot she owned</>,
    titleStr: "The shoulder she forgot she owned",
    footerLine: <>Tension you&apos;ve carried for years<br />can unwind in an hour.</>,
    footerCta: "Deep tissue massage · letgowelllness.com",
    tag: "Deep Tissue",
    body: (
      <>
        <StoryP>
          <Em>&ldquo;I just have a tight shoulder,&rdquo;</Em> she said.
        </StoryP>
        <StoryP mt="0.08in">She had been saying that for two years. She had stopped noticing.</StoryP>
        <StoryP mt="0.08in">The therapist found the knot in under thirty seconds. Slow, intentional pressure. Not pain — recognition.</StoryP>
        <StoryP mt="0.08in">
          She gasped quietly. <Em>&ldquo;I forgot that&apos;s where my arm meets the rest of me.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">By the end of the hour, she could roll her neck without resistance for the first time in seasons.</StoryP>
        <StoryP mt="0.08in">
          She drove home with the heat on her shoulder, and cried — just a little.
        </StoryP>
        <StoryP mt="0.08in">
          The body remembers. <Gold>So do you.</Gold>
        </StoryP>
      </>
    ),
    caption: `"I just have a tight shoulder," she said.
She had been saying that for two years.

The therapist found the knot in under thirty seconds.
Slow, intentional pressure. Not pain — recognition.

"I forgot that's where my arm meets the rest of me."

By the end of the hour, she could roll her neck for the first time in seasons.

Tension you've carried for years can unwind in an hour.

↓ Deep tissue massage · letgowelllness.com

#deeptissuemassage #chronicpain #posture #massage #spa #bodywork #ctspa #waterburyct #letgowellness`,
  },
  {
    id: "spa-swedish",
    slug: "the-first-long-stroke",
    eyebrow: "Let Go Wellness · Swedish Massage",
    title: <>The first long stroke</>,
    titleStr: "The first long stroke",
    footerLine: <>Sometimes you don&apos;t know<br />what you&apos;ve been missing.</>,
    footerCta: "Swedish massage · letgowelllness.com",
    tag: "Swedish",
    body: (
      <>
        <StoryP>She was nervous.</StoryP>
        <StoryP mt="0.08in">She&apos;d never had a massage before. The idea of a stranger touching her shoulders felt strange.</StoryP>
        <StoryP mt="0.08in">She told the therapist this on the way to the room.</StoryP>
        <StoryP mt="0.08in">
          The therapist nodded. <Gold>&ldquo;Then we start slow. We stop whenever you say.&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">
          The first stroke went the length of her back. Long. Warm. Unhurried.
        </StoryP>
        <StoryP mt="0.08in">
          She exhaled — and didn&apos;t realize until later how much air she had been holding.
        </StoryP>
        <StoryP mt="0.08in">
          <Em>&ldquo;Oh,&rdquo;</Em> she said, quietly. <Em>&ldquo;Oh.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">She rebooked before leaving.</StoryP>
      </>
    ),
    caption: `She'd never had a massage before. The idea of a stranger touching her shoulders felt strange.

She told the therapist this on the way to the room.

"Then we start slow. We stop whenever you say."

The first stroke went the length of her back. Long. Warm. Unhurried.

She didn't realize until later how much air she'd been holding.

"Oh," she said, quietly. "Oh."

Sometimes you don't know what you've been missing.

↓ Swedish massage · letgowelllness.com

#swedishmassage #firstmassage #spaday #relaxation #selfcare #wellness #ctspa #waterburyct #letgowellness`,
  },
];

const SPA_CAPTIONS = SPA_STORIES.map((s) => ({
  title: s.titleStr,
  tag: `Spa · ${s.tag}`,
  posterFile: `letgo-spa-${s.slug}.png`,
  caption: s.caption,
}));

// — Service tile (sensorial gold-on-sage card for IG square 1080×1080) —
const ServiceTile = React.forwardRef(function ServiceTile({ service, brand, logoUrl }, ref) {
  const Icon = service.icon;
  return (
    <div
      ref={ref}
      data-testid={`spa-service-tile-${service.id}`}
      className="relative overflow-hidden"
      style={{ width: "3in", height: "3in", background: SAGE_GRADIENT }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center" style={{ padding: "0.32in" }}>
        <Icon className="w-8 h-8 mb-3" style={{ color: "#E8C669" }} />
        <p className="uppercase mb-2" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          {brand || "Let Go Wellness"} · Spa
        </p>
        <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontSize: "0.3in", lineHeight: 1.1, letterSpacing: "0.005em" }}>{service.name}</p>
        <div style={{ width: "0.4in", height: "1px", background: "#E8C669", margin: "0.14in 0" }} />
        <p style={{ color: "rgba(253,251,247,0.85)", fontSize: "0.1in", lineHeight: 1.5, fontFamily: "Manrope, sans-serif", maxWidth: "2.5in" }}>{service.blurb}</p>
        <p className="mt-3" style={{
          background: GOLD_GRAD,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontFamily: "Manrope, sans-serif",
          fontSize: "0.1in",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
        }}>
          {service.duration} · {service.price}
        </p>
      </div>
    </div>
  );
});

// — Treatment menu poster (1080×1920, all 6 services on one printable card) —
const TreatmentMenu = React.forwardRef(function TreatmentMenu({ brand, logoUrl }, ref) {
  return (
    <div
      ref={ref}
      data-testid="spa-treatment-menu"
      className="border border-[#E2DCD0] relative"
      style={{ width: "4in", height: "7.1in", background: SAGE_GRADIENT, overflow: "hidden" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />
      {/* Top brand */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" style={{ paddingTop: "0.32in" }}>
        {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.34in", h: "0.34in" }} />}
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.09in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          Let Go Wellness · Spa
        </p>
      </div>
      {/* Title */}
      <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: "0.95in" }}>
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          Treatment Menu
        </p>
        <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.38in", lineHeight: 1.05, marginTop: "0.08in" }}>
          Rituals for the body
        </p>
        <div style={{ width: "0.45in", height: "1px", background: "#E8C669", marginTop: "0.16in" }} />
      </div>
      {/* Services list */}
      <div className="absolute left-0 right-0" style={{ top: "2.1in", bottom: "1.1in", padding: "0 0.5in", overflow: "hidden" }}>
        {SPA_SERVICES.map((s, i) => (
          <div key={s.id} style={{ marginTop: i === 0 ? 0 : "0.16in" }}>
            <div className="flex items-baseline justify-between" style={{ gap: "0.1in" }}>
              <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, fontSize: "0.17in", lineHeight: 1.1, letterSpacing: "0.005em" }}>{s.name}</p>
              <p style={{
                background: GOLD_GRAD,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                fontFamily: "Manrope, sans-serif",
                fontSize: "0.11in",
                letterSpacing: "0.18em",
                whiteSpace: "nowrap",
                textTransform: "uppercase",
              }}>{s.price}</p>
            </div>
            <p style={{ color: "rgba(253,251,247,0.65)", fontSize: "0.085in", lineHeight: 1.4, fontFamily: "Manrope, sans-serif", letterSpacing: "0.04em", marginTop: "0.04in", textTransform: "uppercase" }}>
              {s.duration}
            </p>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.34in" }}>
        <div style={{ width: "0.45in", height: "1px", background: "rgba(232,198,105,0.5)", marginBottom: "0.14in" }} />
        <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.16in", lineHeight: 1.3, textAlign: "center" }}>
          Book a quiet hour at 808 Highland.
        </p>
        <p className="uppercase mt-3" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.095in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
          letgowelllness.com · 203-437-8193
        </p>
      </div>
    </div>
  );
});

// — Gift Card component (5×3 in landscape, customizable amount) —
const GiftCard = React.forwardRef(function GiftCard({ brand, logoUrl, amount }, ref) {
  return (
    <div
      ref={ref}
      data-testid={`spa-gift-card-${amount}`}
      className="relative overflow-hidden border border-[#E2DCD0]"
      style={{ width: "5in", height: "3in", background: SAGE_GRADIENT }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />
      {/* Decorative gold frame */}
      <div className="absolute pointer-events-none" style={{ top: "0.18in", left: "0.18in", right: "0.18in", bottom: "0.18in", border: "1px solid rgba(232,198,105,0.45)" }} />

      {/* Left column — brand */}
      <div className="absolute top-0 bottom-0 left-0 flex flex-col items-center justify-center" style={{ width: "1.8in", padding: "0.32in" }}>
        {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.6in", h: "0.6in" }} />}
        <p className="font-serif uppercase mt-2" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: "0.18in", letterSpacing: "0.16em", lineHeight: 1 }}>Let Go</p>
        <p style={{ fontFamily: "'Sacramento', cursive", fontSize: "0.18in", letterSpacing: "0.04em", lineHeight: 1, background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", marginTop: "0.04in" }}>Wellness</p>
        <p className="uppercase mt-2" style={{ color: "rgba(253,251,247,0.6)", fontSize: "0.06in", letterSpacing: "0.32em" }}>Spa</p>
      </div>

      {/* Right column — gift details */}
      <div className="absolute top-0 bottom-0 right-0 flex flex-col justify-center" style={{ left: "1.8in", padding: "0.32in 0.4in 0.32in 0.1in" }}>
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.075in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          Gift Certificate
        </p>
        <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.2in", lineHeight: 1.1, marginTop: "0.04in" }}>
          A quiet hour, given.
        </p>

        <p className="mt-2" style={{
          background: GOLD_GRAD,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 600,
          fontSize: "0.6in",
          lineHeight: 1,
        }}>
          ${amount}
        </p>

        {/* Handwriting fields */}
        <div className="mt-3 grid grid-cols-2 gap-3" style={{ paddingRight: "0.1in" }}>
          <div>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.06in", letterSpacing: "0.3em", fontFamily: "Manrope, sans-serif", marginBottom: "0.06in" }}>To</p>
            <div style={{ height: "0.4in", borderBottom: "1px solid rgba(253,251,247,0.4)" }} />
          </div>
          <div>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.06in", letterSpacing: "0.3em", fontFamily: "Manrope, sans-serif", marginBottom: "0.06in" }}>From</p>
            <div style={{ height: "0.4in", borderBottom: "1px solid rgba(253,251,247,0.4)" }} />
          </div>
        </div>

        <p className="uppercase mt-3" style={{ color: "rgba(253,251,247,0.5)", fontSize: "0.06in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
          letgowelllness.com · 203-437-8193
        </p>
      </div>
    </div>
  );
});

// — Before / During / After carousel for first-time spa visitors —
const VISIT_SLIDES = [
  { id: "vs-01-title",  kind: "title",  num: "00", eyebrow: "A guide for first visits", title: <>Your first hour<br />at the spa</>, subtitle: "↓ swipe" },
  { id: "vs-02-before", kind: "body",   num: "01", phase: "Before",  body: "Eat something small. Hydrate. Arrive ten minutes early — park around back, the lot is quieter. Wear what you're comfortable taking off." },
  { id: "vs-03-arrive", kind: "body",   num: "02", phase: "Arrival", body: "You'll be offered tea. The robe is yours. The room will be warm and softly lit. There's no right way to walk in." },
  { id: "vs-04-during", kind: "quote",  num: "03", phase: "During",  quote: "You don't have to talk.\nYou don't have to perform.", footer: "Your therapist will check in. Whisper, gesture, or nod — whatever feels easier." },
  { id: "vs-05-end",    kind: "body",   num: "04", phase: "After",   body: "We'll leave the room so you can re-dress slowly. Water will be waiting. Sit for as long as you need before you stand." },
  { id: "vs-06-home",   kind: "body",   num: "05", phase: "Home",    body: "Drink more water than you think you need. Skip the gym today. Take a long shower. Sleep, if you can — your body just did real work." },
  { id: "vs-07-cta",    kind: "cta",    num: "06", eyebrow: "Ready when you are", title: <>One quiet hour.<br />That&apos;s all it takes.</>, subtitle: "Book your first session.", cta: "letgowelllness.com · 203-437-8193" },
];

const VisitSlide = React.forwardRef(function VisitSlide({ slide, brand, logoUrl }, ref) {
  return (
    <div
      ref={ref}
      data-testid={`visit-slide-${slide.id}`}
      className="relative overflow-hidden"
      style={{ width: "3.6in", height: "3.6in", background: SAGE_GRADIENT }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)", opacity: 0.5 }}
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between" style={{ padding: "0.32in 0.36in 0" }}>
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.085in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          {brand || "Let Go Wellness"} · Spa
        </p>
        <p style={{ color: "rgba(253,251,247,0.45)", fontSize: "0.085in", letterSpacing: "0.22em", fontFamily: "Manrope, sans-serif" }}>
          {slide.num}
        </p>
      </div>

      {/* Slide content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ padding: "0.85in 0.5in" }}>
        {slide.kind === "title" && (
          <>
            {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.8in", h: "0.8in" }} />}
            <p className="uppercase mt-2" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
              {slide.eyebrow}
            </p>
            <p className="font-serif italic mt-2" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.42in", lineHeight: 1.1, whiteSpace: "pre-line" }}>
              {slide.title}
            </p>
            <div style={{ width: "0.5in", height: "1px", background: "#E8C669", margin: "0.2in 0 0.16in" }} />
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.1in", letterSpacing: "0.34em", fontFamily: "Manrope, sans-serif" }}>
              {slide.subtitle}
            </p>
          </>
        )}

        {slide.kind === "body" && (
          <>
            <p style={{
              background: GOLD_GRAD,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontFamily: "Manrope, sans-serif",
              fontSize: "0.11in",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              marginBottom: "0.16in",
            }}>
              {slide.phase}
            </p>
            <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.2in", lineHeight: 1.5, maxWidth: "2.6in" }}>
              {slide.body}
            </p>
          </>
        )}

        {slide.kind === "quote" && (
          <>
            <p className="uppercase mb-3" style={{
              background: GOLD_GRAD,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontFamily: "Manrope, sans-serif",
              fontSize: "0.11in",
              letterSpacing: "0.32em",
            }}>
              {slide.phase}
            </p>
            <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.36in", lineHeight: 1.15, whiteSpace: "pre-line" }}>
              {slide.quote}
            </p>
            <div style={{ width: "0.4in", height: "1px", background: "rgba(232,198,105,0.5)", margin: "0.18in 0" }} />
            <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.78)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.13in", lineHeight: 1.45 }}>
              {slide.footer}
            </p>
          </>
        )}

        {slide.kind === "cta" && (
          <>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
              {slide.eyebrow}
            </p>
            <p className="font-serif italic mt-3" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.36in", lineHeight: 1.15, whiteSpace: "pre-line" }}>
              {slide.title}
            </p>
            <div style={{ width: "0.45in", height: "1px", background: "#E8C669", margin: "0.18in 0" }} />
            <p className="font-serif" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.2in", lineHeight: 1.4 }}>
              {slide.subtitle}
            </p>
            <p className="uppercase mt-6" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.1in", letterSpacing: "0.26em", fontFamily: "Manrope, sans-serif" }}>
              {slide.cta}
            </p>
          </>
        )}
      </div>
    </div>
  );
});

// Small section wrapper, similar to AssetSection on Marketing.jsx.
function SpaSection({ id, eyebrow, title, sub, onDownload, onEmail, onDownloadSvg, downloadLabel = "Download PDF", children }) {
  return (
    <section id={id} className="border-t border-[#E2DCD0] pt-12 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-[#9F5C45] mb-2">{eyebrow}</p>
          <h2 className="font-serif font-light text-3xl md:text-4xl text-[#1C1917] tracking-tight">{title}</h2>
          {sub && <p className="text-[#57534E] mt-3 max-w-2xl text-sm">{sub}</p>}
        </div>
        <div className="flex flex-wrap gap-3 self-end">
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              data-testid={`download-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
            >
              <Download className="w-4 h-4" /> {downloadLabel}
            </button>
          )}
          {onDownloadSvg && (
            <button
              type="button"
              onClick={onDownloadSvg}
              data-testid={`svg-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
              title="Scalable Vector Graphic — resolution-independent."
            >
              <FileCode2 className="w-4 h-4" /> Download SVG
            </button>
          )}
          {onEmail && (
            <button
              type="button"
              onClick={onEmail}
              data-testid={`email-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
            >
              <Mail className="w-4 h-4" /> Email to printer
            </button>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function SpaMarketing() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const logoUrl = resolveImageUrl(site?.brand_logo_url);

  const treatmentMenuRef = useRef(null);
  const serviceTileRefs = useRef({});
  const spaStoryRefs = useRef({});
  const giftCardRefs = useRef({});
  const visitSlideRefs = useRef({});
  const [giftAmount, setGiftAmount] = useState(100);

  const copyCaption = (caption, label) => {
    navigator.clipboard.writeText(caption);
    toast.success(`${label} caption copied`);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-12">
        <Link to="/marketing" className="text-sm text-[#9F5C45] hover:text-[#3A5A40] inline-flex items-center gap-2 mb-4" data-testid="back-to-marketing">
          ← Back to main marketing kit
        </Link>
        <p className="overline text-[#9F5C45] mb-3">Spa · Marketing Kit</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-[#1C1917] tracking-tight">
          Rituals for the body.<br />
          <em className="text-[#3A5A40]">Marketed gently.</em>
        </h1>
        <p className="text-[#57534E] mt-4 max-w-2xl text-base">
          A dedicated marketing kit for the Let Go Wellness spa — treatment menu, service tiles, short stories, and copy-paste captions. Same brand. Different department.
        </p>
        <div className="mt-8 flex flex-wrap gap-2">
          <a href="#treatment-menu" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Treatment Menu</a>
          <a href="#service-tiles" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Service Tiles</a>
          <a href="#spa-stories" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Spa Stories</a>
          <a href="#visit-carousel" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">First-Visit Carousel</a>
          <a href="#gift-cards" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Gift Cards</a>
          <a href="#spa-captions" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Captions</a>
          <Link to="/socials" className="px-4 py-2 btn-primary text-xs uppercase tracking-[0.22em]">Plan Posts →</Link>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Treatment Menu */}
        <SpaSection
          id="treatment-menu"
          eyebrow="Spa · Treatment Menu"
          title="Treatment menu · 4 × 7.1 in"
          sub="The full menu on one portrait card — print-ready PDF at 600 DPI. Perfect for a brochure insert, table tent, or front-desk handout."
          onDownload={() => downloadAsPDF(treatmentMenuRef.current, "letgo-spa-treatment-menu.pdf", 4, 7.1, "#3A5A40")}
          onDownloadSvg={() => downloadAsSvg(treatmentMenuRef.current, "letgo-spa-treatment-menu.svg")}
          onEmail={() => emailToPrinter(treatmentMenuRef.current, "letgo-spa-treatment-menu", 4, 7.1, { backgroundColor: "#3A5A40", subject: "Print order — Let Go Wellness spa treatment menu", notes: "4\" × 7.1\" portrait · matte cardstock 100-110 lb · full-bleed sage background. Quantity: __." })}
        >
          <TreatmentMenu ref={treatmentMenuRef} brand={brand} logoUrl={logoUrl} />
        </SpaSection>

        {/* Service Tiles */}
        <SpaSection
          id="service-tiles"
          eyebrow="Spa · Service Tiles"
          title="Six service tiles · 1080 × 1080 each"
          sub="One Instagram-square tile per service. Use them as a 2 × 3 feed launch, individual posts, or as Story slides. Hover any tile to download a single PNG."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SPA_SERVICES.map((service, i) => (
              <div key={service.id} className="relative group flex justify-center">
                <ServiceTile
                  ref={(el) => (serviceTileRefs.current[service.id] = el)}
                  service={service}
                  brand={brand}
                  logoUrl={logoUrl}
                />
                <button
                  type="button"
                  onClick={() => downloadPosterAtSize(serviceTileRefs.current[service.id], `letgo-spa-tile-${service.id}.png`, 1080, 1080)}
                  data-testid={`download-tile-${service.id}`}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                >
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDFBF7] text-[#1C1917] text-[10px] uppercase tracking-[0.28em]">
                    <Download className="w-3 h-3" /> Tile {String(i + 1).padStart(2, "0")}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </SpaSection>

        {/* Spa Stories */}
        {SPA_STORIES.map((story) => (
          <SpaSection
            key={story.id}
            id={`spa-story-${story.id}`}
            eyebrow={`Spa Story · ${story.tag}`}
            title={`${story.titleStr} · single image`}
            sub={`A short ${story.tag.toLowerCase()} story. Tall portrait image (1080 × 1920), ready for Instagram.`}
            onDownload={() => downloadPosterAtSize(spaStoryRefs.current[story.id], `letgo-spa-${story.slug}.png`, 1080, 1920)}
          >
            <StoryPosterCanvas
              ref={(el) => (spaStoryRefs.current[story.id] = el)}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow={story.eyebrow}
              title={story.title}
              footerLine={story.footerLine}
              footerCta={story.footerCta}
            >
              {story.body}
            </StoryPosterCanvas>
          </SpaSection>
        ))}

        {/* First-visit carousel */}
        <SpaSection
          id="visit-carousel"
          eyebrow="Spa · First-Visit Carousel"
          title="What to expect · 7-slide carousel"
          sub="A swipeable Instagram carousel that walks first-time spa visitors through the entire experience: before, arrival, during, after, home. Brings down the anxiety wall."
          onDownload={async () => {
            const JSZip = (await import("jszip")).default;
            const { toPng } = await import("html-to-image");
            const zip = new JSZip();
            for (const slide of VISIT_SLIDES) {
              const node = visitSlideRefs.current[slide.id];
              if (!node) continue;
              const sourceRect = node.getBoundingClientRect();
              const scale = 1080 / sourceRect.width;
              // eslint-disable-next-line no-await-in-loop
              const dataUrl = await toPng(node, {
                cacheBust: true,
                backgroundColor: "#3A5A40",
                width: 1080,
                height: 1080,
                canvasWidth: 1080,
                canvasHeight: 1080,
                pixelRatio: 1,
                style: {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: `${sourceRect.width}px`,
                  height: `${sourceRect.height}px`,
                },
              });
              zip.file(`letgo-spa-first-visit-${slide.id}.png`, dataUrl.split(",")[1], { base64: true });
            }
            const blob = await zip.generateAsync({ type: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "letgo-spa-first-visit-carousel.zip";
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
            toast.success("7 carousel slides downloaded");
          }}
          downloadLabel="Download all 7 (ZIP)"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VISIT_SLIDES.map((slide, i) => (
              <div key={slide.id} className="relative group flex justify-center">
                <VisitSlide
                  ref={(el) => (visitSlideRefs.current[slide.id] = el)}
                  slide={slide}
                  brand={brand}
                  logoUrl={logoUrl}
                />
                <button
                  type="button"
                  onClick={() => downloadPosterAtSize(visitSlideRefs.current[slide.id], `letgo-spa-first-visit-${slide.id}.png`, 1080, 1080)}
                  data-testid={`download-visit-${slide.id}`}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                >
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDFBF7] text-[#1C1917] text-[10px] uppercase tracking-[0.28em]">
                    <Download className="w-3 h-3" /> Slide {String(i + 1).padStart(2, "0")}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </SpaSection>

        {/* Gift Cards */}
        <SpaSection
          id="gift-cards"
          eyebrow="Spa · Gift Cards"
          title="Printable gift certificates"
          sub="A 5 × 3 inch landscape card with handwriting fields for To / From. Pick a denomination, download the PNG, then print on heavy stock (110 lb recommended)."
        >
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="overline text-[#57534E] mr-2">Amount</span>
            {[50, 75, 100, 150, 200, 250, 300].map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => setGiftAmount(amt)}
                data-testid={`gift-amount-${amt}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.22em] transition-colors ${
                  giftAmount === amt
                    ? "bg-[#3A5A40] text-[#FDFBF7]"
                    : "bg-white text-[#1C1917] border border-[#E2DCD0] hover:bg-[#EAE5D9]"
                }`}
              >
                ${amt}
              </button>
            ))}
            <input
              type="number"
              min="0"
              step="5"
              value={giftAmount}
              onChange={(e) => setGiftAmount(Math.max(0, Number(e.target.value || 0)))}
              data-testid="gift-amount-custom"
              className="ml-2 w-24 px-3 py-2 border border-[#E2DCD0] bg-white text-[#1C1917] text-sm focus:outline-none focus:border-[#3A5A40]"
              placeholder="Custom"
            />
            <button
              type="button"
              onClick={() => downloadAsPDF(giftCardRefs.current[giftAmount], `letgo-spa-gift-card-${giftAmount}.pdf`, 5, 3, "#3A5A40")}
              data-testid="download-gift-card"
              className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 btn-primary text-xs uppercase tracking-[0.22em]"
            >
              <Download className="w-4 h-4" /> Download ${giftAmount} PDF
            </button>
            <button
              type="button"
              onClick={() => downloadAsSvg(giftCardRefs.current[giftAmount], `letgo-spa-gift-card-${giftAmount}.svg`)}
              data-testid="svg-gift-card"
              className="inline-flex items-center gap-2 px-5 py-2.5 btn-ghost text-xs uppercase tracking-[0.22em]"
              title="Scalable Vector Graphic — resolution-independent."
            >
              <FileCode2 className="w-4 h-4" /> SVG
            </button>
            <button
              type="button"
              onClick={() => emailToPrinter(giftCardRefs.current[giftAmount], `letgo-spa-gift-card-${giftAmount}`, 5, 3, { backgroundColor: "#3A5A40", subject: `Print order — $${giftAmount} gift card`, notes: `5\" × 3\" landscape gift card · heavy 110 lb matte stock · full-bleed sage + gold foil if available. Quantity: __.` })}
              data-testid="email-gift-card"
              className="inline-flex items-center gap-2 px-5 py-2.5 btn-ghost text-xs uppercase tracking-[0.22em]"
            >
              <Mail className="w-4 h-4" /> Email to printer
            </button>
          </div>
          <div className="flex flex-wrap gap-6">
            <GiftCard
              ref={(el) => (giftCardRefs.current[giftAmount] = el)}
              brand={brand}
              logoUrl={logoUrl}
              amount={giftAmount}
            />
            <div className="text-sm text-[#57534E] leading-relaxed max-w-sm">
              <p className="overline text-[#9F5C45] mb-2">Tip</p>
              <p>For a polished finish, print on <strong className="text-[#1C1917]">110 lb matte cardstock</strong>. Pair with a small kraft envelope (5¼ × 3¾ in) — sold at most office supply stores. Hand-write the recipient&apos;s name with a fine gold gel pen for the full ritual.</p>
              <p className="mt-3">Print 3-up on letter-size paper using your preferred print shop&apos;s &quot;business card&quot; bleed template, or print a single card at home centered on letter paper and trim to size.</p>
            </div>
          </div>
        </SpaSection>

        {/* Captions */}
        <SpaSection
          id="spa-captions"
          eyebrow="Spa · Captions"
          title="Ready-to-paste spa captions"
          sub="Every story poster comes with a matching caption written in the Let Go voice. Click any one to copy."
          onDownload={() => {
            const text = SPA_CAPTIONS.map((c, i) =>
              `═══════════════════════════════════════════\nSPA POST ${String(i + 1).padStart(2, "0")} · ${c.title}\nPoster file: ${c.posterFile}\n═══════════════════════════════════════════\n\n${c.caption}\n\n`
            ).join("");
            const header = `LET GO WELLNESS · SPA — INSTAGRAM CAPTIONS\nletgowelllness.com\nGenerated ${new Date().toLocaleDateString()}\n\n`;
            const blob = new Blob([header + text], { type: "text/plain;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "letgo-spa-captions.txt";
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
            toast.success("Spa captions downloaded");
          }}
          downloadLabel="Download captions (.txt)"
        >
          <div className="space-y-4 max-w-3xl">
            {SPA_CAPTIONS.map((c, i) => (
              <details
                key={c.posterFile}
                data-testid={`spa-caption-${i + 1}`}
                className="border border-[#E2DCD0] bg-[#FDFBF7] rounded-sm"
              >
                <summary className="cursor-pointer px-5 py-4 flex items-center justify-between text-sm font-medium text-[#1C1917] hover:bg-[#F5F0E6] transition-colors">
                  <span className="flex items-center gap-3">
                    <span className="text-[#9F5C45] text-xs tracking-[0.22em] uppercase">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-base">{c.title}</span>
                  </span>
                  <span className="text-xs uppercase tracking-[0.22em] text-[#57534E]">{c.tag}</span>
                </summary>
                <div className="px-5 pb-5 pt-2 border-t border-[#E2DCD0]">
                  <pre className="whitespace-pre-wrap text-sm text-[#1C1917] leading-relaxed font-sans">{c.caption}</pre>
                  <button
                    type="button"
                    onClick={() => copyCaption(c.caption, c.title)}
                    data-testid={`copy-spa-caption-${i + 1}`}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] btn-ghost"
                  >
                    Copy this caption
                  </button>
                </div>
              </details>
            ))}
          </div>
        </SpaSection>

        {/* Foot note */}
        <div className="my-12 p-6 bg-[#F5F0E6] border border-[#E2DCD0] text-sm text-[#57534E] leading-relaxed">
          <p className="overline text-[#9F5C45] mb-2">One brand, two departments</p>
          <p>
            The spa shares the same sage-and-gold identity as the main clinic — so visitors immediately know they&apos;re in the same trusted place. The voice softens just slightly: less about &ldquo;therapy&rdquo;, more about &ldquo;rituals.&rdquo; If you want a fully separate brand or palette for the spa later, just say the word.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
